import React, { useContext } from 'react';
import cn from 'classnames';
import Image from 'next/future/image';
import removeIcon from '@/public/icons/collection/remove.svg';
import { priceFormater } from '@/settings';
import { Variants } from '@/lib/search-helpers';
import { QsContext } from '@/lib/use-qs';
import useRouterTransition from '@/lib/use-router-transition';
import { isPriceRangeFilterChanged } from './helpers';

const FilterByRemoverPriceRange = (props: {
  variant: string;
  onVariantRemove: () => void;
  isLoading: boolean;
}) => {
  const { variant, onVariantRemove, isLoading } = props;

  return (
    <div className="mb-3 mr-3 flex xs:mb-4 xs:mr-4">
      <span className="rounded-l-full bg-gray-200 py-[8px] pl-3 pr-3 text-xs text-gray-700 xxs:py-2 xxs:pl-4 xxs:text-sm xs:pl-4 xs:pr-6">
        {variant}
      </span>
      <button
        onClick={() => onVariantRemove()}
        type="button"
        className={cn(
          'rounded-r-full !bg-gray-700 pl-2 pr-3 xxs:pl-3 xxs:pr-4',
          {
            'pointer-events-none opacity-30': isLoading,
          },
        )}
      >
        <div className="relative w-[10px] xxs:w-[12px] lg:w-[10px]">
          <Image src={removeIcon} alt="remove" className="w-full" />
        </div>
      </button>
    </div>
  );
};

const FilterByRemover = (props: {
  variant: string;
  variantGroup: Variants;
  onVariantRemove: (value: string, variantGroup: Variants) => void;
  isLoading: boolean;
}) => {
  const { variant, variantGroup, onVariantRemove, isLoading } = props;

  return (
    <div className="mb-3 mr-3 flex xs:mb-4 xs:mr-4 lg:mb-3 lg:mr-3">
      <span className="rounded-l-full bg-gray-200 py-[8px] pl-3 pr-3 text-xs text-gray-700 xxs:py-2 xxs:pl-4 xxs:text-sm xs:pl-4 xs:pr-6 lg:py-2 lg:pr-4 lg:text-xs">
        {variant.replace('Very Good', 'Good').replace(/Oppo/g, 'OPPO')}
      </span>
      <button
        onClick={() => onVariantRemove(variant, variantGroup)}
        type="button"
        className={cn(
          'rounded-r-full !bg-black pl-2 pr-3 xxs:pl-3 xxs:pr-4', // filter by brand objects
          {
            'pointer-events-none opacity-30': isLoading,
          },
        )}
      >
        <div className="relative w-[10px] xxs:w-[12px] lg:w-[10px]">
          <Image src={removeIcon} alt="remove" className="w-full" />
        </div>
      </button>
    </div>
  );
};

function FilterBy(props: {
  selected: Array<{ variant: string; variantGroup: Variants }>;
  priceRangeValue: { min: number; max: number };
  priceRange: { min?: string; max?: string };
  onPriceRangeRemove: () => void;
}) {
  const { selected, priceRange, priceRangeValue, onPriceRangeRemove } = props;
  const [qs, updateQs] = useContext(QsContext);
  const isTransitioning = useRouterTransition();

  const onAllVariantRemove = () => {
    updateQs({
      categories: undefined,
      brand: undefined,
      dealTypes: [].join('|'),
    });

    onPriceRangeRemove();
  };

  const onVariantRemove = (variant: string, variantGroup: Variants) => {
    const nextQs = qs[variantGroup]
      ?.split('|')
      .filter((el: any) => el !== variant)
      .join('|');

    return updateQs({ [variantGroup]: nextQs });
  };

  const priceRangeMin = Number(priceRange.min);
  const priceRangeMax = Number(priceRange.max);

  const isPriceRangeChanged = isPriceRangeFilterChanged(
    priceRange,
    priceRangeValue,
  );

  return (
    <div className="mb-0 block border-x-0 border-y !border-t-0 px-5 py-2 lg:border lg:border-x lg:!border-t lg:border-gray-100 lg:px-5 lg:py-5">
      <div className="mb-3 flex items-center justify-between">
        <button
          onClick={() => onAllVariantRemove()}
          type="button"
          className={cn(
            'place-self-end bg-gray-200 px-[12px] py-[6px] text-sm xs:px-3 xs:py-2 lg:text-xs',
            {
              'pointer-events-none opacity-30': isTransitioning,
            },
          )}
        >
          Clear All
        </button>
      </div>
      <div className="flex flex-wrap justify-start">
        {isPriceRangeChanged && (
          <FilterByRemoverPriceRange
            variant={`${priceFormater.long(
              priceRangeMin > 0 ? priceRangeMin : priceRangeValue.min,
            )} - ${priceFormater.long(
              priceRangeMax > 0 ? priceRangeMax : priceRangeValue.max,
            )}`}
            onVariantRemove={onPriceRangeRemove}
            isLoading={isTransitioning}
          />
        )}
        {selected.map((selectedItem, index) => (
          <FilterByRemover
            variant={selectedItem.variant}
            variantGroup={selectedItem.variantGroup}
            onVariantRemove={onVariantRemove}
            isLoading={isTransitioning}
            key={index} // Make sure 'key' is unique for each item
          />
        ))}
      </div>
    </div>
  );
}

export default FilterBy;
